import { Card, Icon, PickerChip } from "@/components/kit";
import type { Supplier } from "./lib/useStock";
import type { Draft } from "./lib/itemDraft";

/**
 * AN ITEM'S DEFINITION, ONCE — the same fields on f2's edit pane and on f3's
 * full-width New item page, because they are the same nine facts and a clinic
 * that learns them in one place should not meet a different form in the other.
 *
 * DEFINITIONS LIVE APART FROM QUANTITIES (f2's own footnote). Nothing here is
 * a number on a shelf: the minimum is a judgement, the cost is what the clinic
 * expects to pay, and both are set by the person who defines the item. What is
 * actually there arrives through a receipt, a use or a count, and never
 * through this form.
 */

/** The units a clinic measures its shelf in, and the boards' own words. */
const UNITS = [
  "vial",
  "ampoule",
  "piece",
  "pack",
  "box × 20",
  "box × 100",
  "bottle",
  "tube",
  "roll",
];

export function ItemFields({
  draft,
  onChange,
  suppliers,
  categories,
  perMonth,
  lastPaid,
  compact,
}: {
  draft: Draft;
  onChange: (next: Partial<Draft>) => void;
  suppliers: Supplier[];
  categories: string[];
  /** The measured rate, when the item has one — what the order size is judged against. */
  perMonth?: number | null;
  /** What the last delivery charged, as the empty Cost field's placeholder. */
  lastPaid?: string;
  /** The pane is one column; the New item page is two. */
  compact?: boolean;
}) {
  const set = (next: Partial<Draft>) => onChange(next);
  const cols = compact ? 1 : 2;
  const supplier =
    suppliers.find((s) => s.id === draft.preferredSupplierId) ?? null;

  /* The defaults the board states outright: the threshold is the minimum, and
     the order size is about a month of use. Shown rather than silently
     applied, so the person setting them can see where they came from. */
  const suggestedQty = perMonth ? String(perMonth) : "";

  return (
    <>
      <Card>
        <label className="t-ovl mut" htmlFor="it-name">
          Name
        </label>
        <input
          id="it-name"
          className="fld t-strong mt4"
          placeholder="what the clinic calls it"
          value={draft.name}
          onChange={(e) => set({ name: e.target.value })}
        />
      </Card>

      <Card>
        <label className="t-ovl mut" htmlFor="it-barcode">
          Barcode
        </label>
        <input
          id="it-barcode"
          className="fld t-strong mt4"
          placeholder="scan or type the manufacturer barcode"
          value={draft.barcode ?? ""}
          onChange={(e) => set({ barcode: e.target.value })}
        />
        <p className="t-cap mut mt8">
          {draft.barcode?.trim()
            ? "receiving and counting jump straight to this row when it is scanned"
            : "without one, this item is found by name"}
        </p>
      </Card>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: 8,
        }}
      >
        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Unit</p>
          <p className="mt4">
            <PickerChip
              label={`${draft.unit || "Pick a unit"} ▾`}
              ariaLabel="Unit"
              kind={draft.unit ? "brand" : "outline"}
              value={draft.unit ?? ""}
              onChange={(v) => set({ unit: v || null })}
              options={[
                { value: "", label: "No unit" },
                ...UNITS.map((u) => ({ value: u, label: u })),
                ...(draft.unit && !UNITS.includes(draft.unit)
                  ? [{ value: draft.unit, label: draft.unit }]
                  : []),
              ]}
            />
          </p>
        </Card>

        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Category</p>
          <p className="mt4">
            <PickerChip
              label={`${draft.category || "Uncategorised"} ▾`}
              ariaLabel="Category"
              kind={draft.category ? "brand" : "outline"}
              value={draft.category ?? ""}
              onChange={(v) => set({ category: v || null })}
              options={[
                { value: "", label: "Uncategorised" },
                ...categories.map((c) => ({ value: c, label: c })),
              ]}
            />
          </p>
        </Card>

        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="it-min">
            Min level
          </label>
          <input
            id="it-min"
            className="fld t-strong mt4 num"
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="never called low"
            value={draft.min}
            onChange={(e) => set({ min: e.target.value })}
          />
          <p className="t-cap mut mt8">
            {perMonth
              ? `about ${perMonth} a month — half that is roughly a fortnight's cover`
              : "below this, the shelf calls it low"}
          </p>
        </Card>

        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="it-cost">
            Cost
          </label>
          <input
            id="it-cost"
            className="fld t-strong mt4 num"
            inputMode="decimal"
            placeholder={lastPaid ? `${lastPaid} last paid` : 'per unit'}
            value={draft.cost}
            onChange={(e) => set({ cost: e.target.value })}
          />
          <p className="t-cap mut mt8">
            what the clinic expects to pay — a reorder is priced from it, and so
            is a write-off
          </p>
        </Card>
      </div>

      <Card>
        <p className="t-ovl mut">Preferred supplier</p>
        <p className="mt4">
          <PickerChip
            label={`${supplier?.name ?? "Nobody in particular"} ▾`}
            ariaLabel="Preferred supplier"
            kind={draft.preferredSupplierId ? "brand" : "outline"}
            value={draft.preferredSupplierId ?? ""}
            onChange={(v) => set({ preferredSupplierId: v || null })}
            options={[
              { value: "", label: "Nobody in particular" },
              ...suppliers.map((s) => ({
                value: s.id,
                label: s.name,
                hint: s.contactName ?? s.contactPhone ?? undefined,
              })),
            ]}
          />
        </p>
        {suppliers.length === 0 ? (
          <p className="t-cap mut mt8">
            No supplier is in the Directory yet — an item can be defined without
            one and receive against a typed name.
          </p>
        ) : null}
      </Card>

      <Card
        style={
          draft.batchTracked ? { borderColor: "var(--brand-300)" } : undefined
        }
      >
        <label
          className="flex"
          style={{ gap: 8, alignItems: "center", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={!!draft.batchTracked}
            onChange={(e) => set({ batchTracked: e.target.checked })}
          />
          <span className="t-strong grow">Batch &amp; expiry tracked</span>
        </label>
        <p className="t-cap mut mt8">
          drugs yes · gauze no — drives FEFO and the expiry lane, and makes the
          expiry date required at receipt
        </p>
      </Card>

      <Card
        style={
          draft.autoOrderEnabled
            ? { borderColor: "var(--brand-300)" }
            : undefined
        }
      >
        <label
          className="flex"
          style={{ gap: 8, alignItems: "center", cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={!!draft.autoOrderEnabled}
            onChange={(e) =>
              set({
                autoOrderEnabled: e.target.checked,
                /* The board's own defaults, filled in the moment it is turned
                   on rather than left blank for somebody to discover. */
                threshold: draft.threshold || draft.min,
                orderQty: draft.orderQty || suggestedQty,
              })
            }
          />
          <span className="t-strong grow">Auto-order when low</span>
        </label>
        {draft.autoOrderEnabled ? (
          <>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <label className="t-ovl mut" htmlFor="it-thr">
                  When qty ≤
                </label>
                <input
                  id="it-thr"
                  className="fld t-strong mt4 num"
                  type="number"
                  min={0}
                  inputMode="numeric"
                  placeholder={draft.min || "the minimum"}
                  value={draft.threshold}
                  onChange={(e) => set({ threshold: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="t-ovl mut" htmlFor="it-qty">
                  Order
                </label>
                <input
                  id="it-qty"
                  className="fld t-strong mt4 num"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  placeholder={suggestedQty || "how many"}
                  value={draft.orderQty}
                  onChange={(e) => set({ orderQty: e.target.value })}
                />
              </div>
            </div>
            <p className="t-cap mut mt8">
              defaults: threshold = min level · quantity ≈ one month of use
              {perMonth && draft.orderQty
                ? ` — at ≈${perMonth}/mo, ${draft.orderQty} covers about ${Math.max(
                    1,
                    Math.round((Number(draft.orderQty) / perMonth) * 4.3),
                  )} weeks`
                : ""}
            </p>
          </>
        ) : (
          <p className="t-cap mut mt8">
            off — this item is reordered by somebody noticing, not by the shelf
          </p>
        )}
      </Card>
    </>
  );
}


/**
 * `g_newitem` / `g_k3` — the same facts as {@link ItemFields}, laid out the way
 * the New item boards draw them: the name and the barcode on top, the five
 * short facts in one row, then ONE card per decision (auto-order, batch
 * tracking). The row wraps to two lines on a phone.
 */
export function NewItemFields({
  draft,
  onChange,
  suppliers,
  categories,
}: {
  draft: Draft;
  onChange: (next: Partial<Draft>) => void;
  suppliers: Supplier[];
  categories: string[];
}) {
  const set = (next: Partial<Draft>) => onChange(next);
  const supplier = suppliers.find((s) => s.id === draft.preferredSupplierId) ?? null;
  const tgl = (on: boolean) => <span className={`tgl${on ? ' on' : ''}`} aria-hidden="true" />;
  return (
    <>
      <Card>
        <label className="t-ovl mut" htmlFor="it-name">Name</label>
        <input id="it-name" className="fld t-strong mt4" placeholder="what the clinic calls it"
          value={draft.name} onChange={(e) => set({ name: e.target.value })} />
      </Card>

      {/* A scanner types into a focused field, so the tile IS the field. */}
      <Card>
        <label className="flex" style={{ gap: 10, alignItems: 'center' }} htmlFor="it-barcode">
          <Icon name="barcode" />
          <input id="it-barcode" className="fld grow" placeholder="Scan or type the manufacturer barcode"
            value={draft.barcode ?? ''} onChange={(e) => set({ barcode: e.target.value })} />
        </label>
      </Card>

      <div className="stock-newitem-facts">
        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Unit</p>
          <p className="mt4">
            <PickerChip
              label={`${draft.unit || 'Pick a unit'} ▾`}
              ariaLabel="Unit"
              kind={draft.unit ? 'brand' : 'outline'}
              value={draft.unit ?? ''}
              onChange={(v) => set({ unit: v || null })}
              options={[
                { value: '', label: 'No unit' },
                ...UNITS.map((u) => ({ value: u, label: u })),
                ...(draft.unit && !UNITS.includes(draft.unit) ? [{ value: draft.unit, label: draft.unit }] : []),
              ]}
            />
          </p>
        </Card>
        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="it-min">Min level</label>
          <input id="it-min" className="fld t-strong mt4 num" type="number" min={0} inputMode="numeric"
            placeholder="never called low" value={draft.min} onChange={(e) => set({ min: e.target.value })} />
        </Card>
        <Card style={{ margin: 0 }}>
          <label className="t-ovl mut" htmlFor="it-cost">Cost</label>
          <input id="it-cost" className="fld t-strong mt4 num" inputMode="decimal" placeholder="per unit"
            value={draft.cost} onChange={(e) => set({ cost: e.target.value })} />
        </Card>
        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Category</p>
          <p className="mt4">
            <PickerChip
              label={`${draft.category || 'Uncategorised'} ▾`}
              ariaLabel="Category"
              kind={draft.category ? 'brand' : 'outline'}
              value={draft.category ?? ''}
              onChange={(v) => set({ category: v || null })}
              options={[{ value: '', label: 'Uncategorised' }, ...categories.map((c) => ({ value: c, label: c }))]}
            />
          </p>
        </Card>
        <Card style={{ margin: 0 }}>
          <p className="t-ovl mut">Preferred supplier</p>
          <p className="mt4">
            <PickerChip
              label={`${supplier?.name ?? 'Nobody in particular'} ▾`}
              ariaLabel="Preferred supplier"
              kind={draft.preferredSupplierId ? 'brand' : 'outline'}
              value={draft.preferredSupplierId ?? ''}
              onChange={(v) => set({ preferredSupplierId: v || null })}
              options={[
                { value: '', label: 'Nobody in particular' },
                ...suppliers.map((s) => ({ value: s.id, label: s.name, hint: s.contactName ?? s.contactPhone ?? undefined })),
              ]}
            />
          </p>
        </Card>
      </div>

      <div className="stock-newitem-cards">
        <Card style={draft.autoOrderEnabled ? { borderColor: 'var(--brand-300)' } : undefined}>
          <label className="flex" style={{ gap: 12, alignItems: 'center', cursor: 'pointer' }}>
            <span className="t-strong grow">Auto-order when low</span>
            <input type="checkbox" className="sr-only" checked={!!draft.autoOrderEnabled}
              onChange={(e) => set({
                autoOrderEnabled: e.target.checked,
                threshold: draft.threshold || draft.min,
              })} />
            {tgl(!!draft.autoOrderEnabled)}
          </label>
          {draft.autoOrderEnabled ? (
            <>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <div className="card" style={{ flex: 1, margin: 0 }}>
                  <label className="t-ovl mut" htmlFor="it-thr">When qty ≤</label>
                  <input id="it-thr" className="fld t-strong mt4 num" type="number" min={0} inputMode="numeric"
                    placeholder={draft.min || 'the minimum'} value={draft.threshold}
                    onChange={(e) => set({ threshold: e.target.value })} />
                </div>
                <div className="card" style={{ flex: 1, margin: 0 }}>
                  <label className="t-ovl mut" htmlFor="it-qty">Order</label>
                  <input id="it-qty" className="fld t-strong mt4 num" type="number" min={1} inputMode="numeric"
                    placeholder="how many" value={draft.orderQty}
                    onChange={(e) => set({ orderQty: e.target.value })} />
                </div>
              </div>
              <p className="t-cap mut mt8">defaults: threshold = min level · quantity ≈ one month of use</p>
            </>
          ) : (
            <p className="t-cap mut mt8">off — this item is reordered by somebody noticing, not by the shelf</p>
          )}
        </Card>

        <Card style={draft.batchTracked ? { borderColor: 'var(--brand-300)' } : undefined}>
          <label className="flex" style={{ gap: 12, alignItems: 'center', cursor: 'pointer' }}>
            <span className="t-strong grow">Batch &amp; expiry tracked</span>
            <input type="checkbox" className="sr-only" checked={!!draft.batchTracked}
              onChange={(e) => set({ batchTracked: e.target.checked })} />
            {tgl(!!draft.batchTracked)}
          </label>
          <p className="t-cap mut mt8">
            drugs yes · gauze no — drives FEFO and the expiry lane, and makes the expiry date required at receipt
          </p>
        </Card>
      </div>
    </>
  );
}
